import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridApi,
  ICellEditorParams,
  ValueFormatterParams,
  ValueSetterParams,
} from "ag-grid-community";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import { formatDisplayDate } from "../../utils/date";
import {
  ParsedTransactionRow,
  VALID_TRANSACTION_TYPES,
} from "../../types/BulkGrantUpdate";
import {
  useUpdateGrantTransactions,
  useValidateTransactions,
} from "../../queries/bulkGrantUpdate";
import SearchInput from "../../components/shared/SearchInput";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";

interface BulkGrantUpdatePreviewProps {
  data: ParsedTransactionRow[];
  onCancel: () => void;
  onSuccess: () => void;
}

// Cell renderer to show validation status
const ValidationCellRenderer = (params: any) => {
  const row: ParsedTransactionRow = params.data;

  if (row.isValid) {
    return (
      <div className="flex items-center gap-1 text-green-600">
        <Icon icon="mdi:check-circle" height={16} />
        <span className="text-xs">Valid</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1 text-red-600"
      title={row.errors.join(", ")}
    >
      <Icon icon="mdi:alert-circle" height={16} />
      <span className="text-xs">Error</span>
    </div>
  );
};

// Cell renderer to show errors
const ErrorsCellRenderer = (params: any) => {
  const row: ParsedTransactionRow = params.data;

  if (!row.errors || row.errors.length === 0) {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <div className="text-xs text-red-600 py-1">
      <ul className="list-disc list-inside">
        {row.errors.map((err, idx) => (
          <li key={idx}>{err}</li>
        ))}
      </ul>
    </div>
  );
};

const BulkGrantUpdatePreview: React.FC<BulkGrantUpdatePreviewProps> = ({
  data,
  onCancel,
  onSuccess,
}) => {
  const gridApi = useRef<GridApi | any>(null);
  const [rowData, setRowData] = useState<ParsedTransactionRow[]>([]);

  const { mutate: updateTransactions, isLoading: isUpdating } =
    useUpdateGrantTransactions();
  const { mutate: validateTransactions, isLoading: isValidating } =
    useValidateTransactions();

  const currencyType = getCurrencyType();

  // Initialize rowData when data prop changes
  useEffect(() => {
    if (data && Array.isArray(data) && data.length > 0) {
      setRowData([...data]);
    }
  }, [data]);

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    setTimeout(() => {
      params.api.sizeColumnsToFit();
    }, 100);
  };

  // Value setter for editable cells
  const valueSetter = (params: ValueSetterParams): boolean => {
    const field = params.colDef.field;
    if (!field) return false;

    const newValue = params.newValue;

    if ((params.data as any)[field] === newValue) return false;

    (params.data as any)[field] = newValue;
    return true;
  };

  // Cell editor selector based on field type
  const cellEditorSelector = (params: ICellEditorParams) => {
    const field = params.colDef.field;

    if (field === "transactionType") {
      return {
        component: "agSelectCellEditor",
        params: { values: [...VALID_TRANSACTION_TYPES] },
      };
    }

    if (field === "transactionDate") {
      return {
        component: "agDateCellEditor",
        params: {
          min: "1900-01-01",
          max: "2100-12-31",
        },
      };
    }

    if (field === "noOfOptions") {
      const type = params.data.transactionType;
      const allowDecimals = type === "CASHED_OUT" || type === "LAPSED";
      return {
        component: "agNumberCellEditor",
        params: {
          precision: allowDecimals ? 4 : 0,
          step: allowDecimals ? 0.0001 : 1,
        },
      };
    }

    if (field === "fmv") {
      return { component: "agNumberCellEditor" };
    }

    return { component: "agTextCellEditor" };
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Status",
        field: "isValid",
        width: 110,
        minWidth: 110,
        cellRenderer: ValidationCellRenderer,
        pinned: "left",
        filter: "agSetColumnFilter",
        valueGetter: (params) => (params.data.isValid ? "Valid" : "Invalid"),
        filterParams: {
          values: ["Valid", "Invalid"],
        },
      },
      {
        headerName: "Grant ID",
        field: "grantId",
        minWidth: 150,
        editable: false,
        filter: "agSetColumnFilter",
      },
      {
        headerName: "Employee Name",
        field: "employeeName",
        minWidth: 180,
        editable: false,
        filter: "agSetColumnFilter",
      },
      {
        headerName: "Transaction Date",
        field: "transactionDate",
        minWidth: 150,
        editable: true,
        valueSetter,
        cellEditorSelector,
        valueGetter: (params) => {
          const val = params.data.transactionDate;
          if (!val) return null;
          const date = new Date(val);
          return Number.isNaN(date.getTime()) ? null : date;
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return "-";
          return formatDisplayDate(params.value);
        },
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Transaction Type",
        field: "transactionType",
        minWidth: 150,
        editable: true,
        valueSetter,
        valueGetter: (params) => params.data.transactionType.replace(/_/g, " "),
        cellEditorSelector,
        filter: "agSetColumnFilter",
      },
      {
        headerName: "No. of Options",
        field: "noOfOptions",
        minWidth: 130,
        editable: true,
        valueSetter,
        cellEditorSelector,
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return "-";
          const transactionType = params.data?.transactionType;
          const allowDecimals =
            (transactionType === "CASHED_OUT" ||
              transactionType === "LAPSED") &&
            params.data?.isFractional;
          return Number(params.value).toLocaleString(currencyType, {
            minimumFractionDigits: 0,
            maximumFractionDigits: allowDecimals ? 4 : 0,
          });
        },
        filter: "agNumberColumnFilter",
        fractionDigits: (params: any) => {
          const { isFractional, transactionType } = params.data || {};
          if (
            (transactionType === "CASHED_OUT" ||
              transactionType === "LAPSED") &&
            isFractional
          ) {
            return isFractional ? 4 : 0;
          }
          return 0;
        },
      },
      {
        headerName: "FMV",
        field: "fmv",
        minWidth: 120,
        editable: true,
        valueSetter,
        cellEditorSelector,
        valueGetter: (params) => {
          const val = params.data.fmv;
          if (val === undefined || val === null || val === "") return null;
          const num = Number(val);
          return Number.isNaN(num) ? null : num;
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return "-";
          return formatCurrency(Number(params.value), currencyType);
        },
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Buyer Name",
        field: "buyerName",
        minWidth: 150,
        editable: true,
        valueSetter,
        filter: "agSetColumnFilter",
      },
      {
        headerName: "Errors",
        field: "errors",
        minWidth: 250,
        flex: 1,
        editable: false,
        cellRenderer: ErrorsCellRenderer,
        autoHeight: true,
        wrapText: true,
      },
    ],
    [currencyType]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      menuTabs: ["filterMenuTab"] as any,
      filterParams: {
        buttons: ["reset"],
      },
      cellStyle: { paddingTop: "12px", lineHeight: "20px" },
    }),
    []
  );

  const handleUpdate = () => {
    const validSelected = rowData.every((row) => row.isValid);

    if (!validSelected) {
      toast.error("Please ensure all rows are valid before saving.");
      return;
    }
    updateTransactions(rowData, {
      onSuccess: (result) => {
        if (result.data) {
          setRowData(result.data);
        }

        if (result.created > 0) {
          toast.success(
            `Successfully created ${result.created} transaction(s)`
          );
          onSuccess();
        }

        if (result.errors && result.errors.length > 0) {
          toast.error(`Please review errors before proceeding.`);
        }
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Error updating transactions"
        );
      },
    });
  };

  const handleValidate = () => {
    validateTransactions(
      rowData.map((row) => ({ ...row, errors: [] })),
      {
        onSuccess: (result) => {
          if (result.data) {
            setRowData(result.data);
            toast.success("Validation completed");
          }
        },
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.message || "Error validating transactions"
          );
        },
      }
    );
  };

  const validCount = rowData.filter((row) => row.isValid).length;
  const invalidCount = rowData.filter((row) => !row.isValid).length;

  if (!rowData || rowData.length === 0) {
    return (
      <VStack className="w-full h-full bg-white border border-gray-200 rounded-lg min-h-[600px] items-center justify-center">
        <Icon
          icon="mdi:loading"
          className="animate-spin text-gray-400"
          height={48}
        />
        <p className="text-gray-500 mt-4">Loading data...</p>
      </VStack>
    );
  }

  return (
    <VStack className="w-full bg-white border border-gray-200 rounded-lg min-h-[600px]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Preview Grant Transactions
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Review and edit transactions before saving. Only valid transactions
            can be selected.
          </p>
        </div>
        <HStack className="items-center gap-4">
          <SearchInput
            placeholder="Search..."
            onChange={(e: any) => {
              if (gridApi.current) {
                gridApi.current.setQuickFilter(e.target.value);
              }
            }}
          />
        </HStack>
      </div>

      {/* Stats Bar */}
      <HStack className="flex items-center gap-6 px-6 py-3 bg-gray-50 border-b border-gray-100 justify-between">
        <HStack className="gap-2">
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-600">Total Rows:</span>
            <span className="font-semibold text-gray-800">
              {rowData.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Icon
              icon="mdi:check-circle"
              className="text-green-600"
              height={16}
            />
            <span className="text-sm text-gray-600">Valid:</span>
            <span className="font-semibold text-green-600">{validCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon
              icon="mdi:alert-circle"
              className="text-red-600"
              height={16}
            />
            <span className="text-sm text-gray-600">Invalid:</span>
            <span className="font-semibold text-red-600">{invalidCount}</span>
          </div>
        </HStack>
        <HStack className="gap-1 px-4">
          <div>
            <p className="text-sm text-gray-500">
              <Icon
                icon="mdi:information-outline"
                className="inline mr-1"
                height={16}
              />
              Click on cells to edit values.
            </p>
          </div>
        </HStack>
      </HStack>

      {/* Grid */}
      <div
        className="ag-theme-material w-full px-4 py-4"
        style={{ height: "500px", width: "100%" }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          rowSelection="multiple"
          rowData={rowData}
          rowClass="border-t border-dashed"
          suppressRowClickSelection={true}
          stopEditingWhenCellsLoseFocus={true}
          rowMultiSelectWithClick={true}
          singleClickEdit={true}
          getRowStyle={(params) => {
            if (!params.data?.isValid) {
              return { backgroundColor: "#fef2f2" };
            }
            return undefined;
          }}
        />
      </div>

      {/* Footer */}
      <HStack className="justify-between items-center gap-4 px-6 py-4 border-t border-gray-100">
        <HStack>
          <ButtonPrimary1
            className="text-red-500 border-red-500 hover:bg-red-50"
            onClick={onCancel}
            disabled={isUpdating || isValidating}
          >
            Discard & Back
          </ButtonPrimary1>
        </HStack>
        <HStack className="gap-3">
          <ButtonPrimary1
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
            onClick={handleValidate}
            loading={isValidating}
            disabled={isUpdating}
          >
            Validate All
          </ButtonPrimary1>
          <ButtonPrimary
            onClick={handleUpdate}
            disabled={
              rowData.every((row) => row.isValid) === false || isValidating
            }
            loading={isUpdating}
          >
            Save All
          </ButtonPrimary>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default BulkGrantUpdatePreview;
